import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { Avatar } from '../components/Avatar';
import { LeadsSettingsModal } from '../components/LeadsSettingsModal';
import { ConvertLeadModal } from '../components/ConvertLeadModal';
import { ImportGoogleContactsModal } from '../components/ImportGoogleContactsModal';
import type { LeadSource, LeadStatus, LeadSummary, LeadsListResponse } from '../types';

// 'converted' deliberately omitted — converted leads live as Clients
// now and the leads page hides them by default. Re-adding the chip
// would surface a permanent dead-end for a status the user has been
// told moves the record to /clients.
const STATUS_KEYS: LeadStatus[] = ['new', 'contacted', 'qualified', 'archived'];
const SOURCE_KEYS: LeadSource[] = ['facebook', 'site', 'manual', 'whatsapp', 'referral'];

const STATUS_BADGE_CLS: Record<LeadStatus, string> = {
  new: 'b-pending',
  contacted: 'b-pending',
  qualified: 'b-open',
  converted: 'b-open',
  archived: 'b-closed',
};

export function Leads() {
  const { t, i18n } = useTranslation('leads');
  const navigate = useNavigate();
  const [data, setData] = useState<LeadsListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<LeadStatus | 'all'>('all');
  const [sourceFilter, setSourceFilter] = useState<LeadSource | 'all'>('all');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [converting, setConverting] = useState<LeadSummary | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (sourceFilter !== 'all') params.set('source', sourceFilter);
      const d = await apiFetch<LeadsListResponse>(`/api/leads?${params}`);
      setData(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, sourceFilter]);

  async function patchStatus(lead: LeadSummary, next: LeadStatus) {
    try {
      const updated = await apiFetch<LeadSummary>(`/api/leads/${lead.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: next }),
      });
      setData((prev) =>
        prev
          ? { ...prev, items: prev.items.map((l) => (l.id === lead.id ? updated : l)) }
          : prev,
      );
    } catch {
      // ignore — load() on next render or user-triggered will refetch
    }
  }

  const totals = data?.counts ?? { new: 0, contacted: 0, qualified: 0, converted: 0, archived: 0 };

  const filteredItems = useMemo(() => data?.items ?? [], [data]);

  return (
    <div>
      <div className="metrics" style={{ marginBottom: 18 }}>
        {STATUS_KEYS.map((s) => (
          <div
            key={s}
            className="metric"
            onClick={() => setStatusFilter(s)}
            style={{
              cursor: 'pointer',
              outline: statusFilter === s ? '2px solid var(--accent)' : 'none',
            }}
          >
            <div className="metric-label">{t(`status.${s}`)}</div>
            <div className="metric-val">{totals[s]}</div>
          </div>
        ))}
      </div>

      <div className="filter-row" style={{ marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div
          className={`type-btn ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => setStatusFilter('all')}
          style={{ flex: 'unset', padding: '6px 14px' }}
        >
          {t('filters.all')}
        </div>
        {STATUS_KEYS.map((s) => (
          <div
            key={s}
            className={`type-btn ${statusFilter === s ? 'active' : ''}`}
            onClick={() => setStatusFilter(s)}
            style={{ flex: 'unset', padding: '6px 14px' }}
          >
            {t(`status.${s}`)}
          </div>
        ))}
        <div style={{ flex: 1 }} />
        <select
          className="inline-select"
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as LeadSource | 'all')}
        >
          <option value="all">
            {t('filters.by_source')} {t('filters.all')}
          </option>
          {SOURCE_KEYS.map((s) => (
            <option key={s} value={s}>
              {t(`source.${s}`)}
            </option>
          ))}
        </select>
        <button className="btn-cancel" onClick={() => setImportOpen(true)}>
          📇 {t('import.button')}
        </button>
        <button className="btn-cancel" onClick={() => setSettingsOpen(true)}>
          ⚙ {t('settings.title')}
        </button>
      </div>

      <div className="full-panel">
        <div className="panel-header">{t('title')}</div>
        {loading && !data ? (
          <div className="empty">{t('loading')}</div>
        ) : error ? (
          <div className="empty">{t('error')}</div>
        ) : filteredItems.length === 0 ? (
          <div className="empty">{t('empty')}</div>
        ) : (
          filteredItems.map((lead) => {
            const display = lead.name?.trim() || lead.email || lead.phone || '—';
            const sub = [
              lead.email,
              lead.phone,
              lead.area,
              t(`source.${lead.source}`),
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <div
                key={lead.id}
                className="case-row"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/leads/${lead.id}`)}
              >
                <Avatar name={display} size="md" />
                <div className="case-info">
                  <div className="case-name">{display}</div>
                  <div className="case-sub">{sub}</div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                  {new Date(lead.createdAt).toLocaleDateString(i18n.language, {
                    day: '2-digit',
                    month: 'short',
                  })}
                </div>
                <select
                  className="inline-select"
                  value={lead.status}
                  disabled={lead.status === 'converted'}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => patchStatus(lead, e.target.value as LeadStatus)}
                  style={{ marginInlineStart: 8 }}
                >
                  {STATUS_KEYS.filter(
                    (s) => s !== 'converted' || lead.status === 'converted',
                  ).map((s) => (
                    <option key={s} value={s}>
                      {t(`status.${s}`)}
                    </option>
                  ))}
                </select>
                <span className={`badge ${STATUS_BADGE_CLS[lead.status]}`}>
                  {t(`status.${lead.status}`)}
                </span>
                {lead.status !== 'converted' && (
                  <button
                    className="btn-cancel"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConverting(lead);
                    }}
                    style={{ marginInlineStart: 8 }}
                  >
                    {t('actions.convert')}
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>

      {settingsOpen && <LeadsSettingsModal onClose={() => setSettingsOpen(false)} />}
      {importOpen && (
        <ImportGoogleContactsModal
          onImported={() => {
            setImportOpen(false);
            void load();
          }}
          onClose={() => setImportOpen(false)}
        />
      )}
      {converting && (
        <ConvertLeadModal
          lead={converting}
          onClose={() => setConverting(null)}
          onConverted={() => {
            setConverting(null);
            void load();
          }}
        />
      )}
    </div>
  );
}
